import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { QRCodeSVG } from 'qrcode.react';
import Modal from '../../../controls/Modal';
import LabeledInput from '../../../controls/LabeledInput';
import LabeledSelect from '../../../controls/LabeledSelect';
import AppMediaList from '../../../controls/media/AppMediaList';
import { CoinIcon } from '../../../../model/icons';
import {
    POINT_TYPE, POINT_STATUS, POINT_TYPE_LABELS, ALLOWED_PRICES, ALLOWED_COEFFICIENTS
} from '../../../../../../hooks/useFairEditState';
import { pointStatusChip } from '../fairModel';

const TYPE_OPTIONS = Object.entries(POINT_TYPE_LABELS).map(([key, value]) => ({ key, value }));
const PRICE_OPTIONS = ALLOWED_PRICES.map(p => ({ key: p, value: `${p} монет` }));
const COEF_OPTIONS = ALLOWED_COEFFICIENTS.map(c => ({ key: c, value: `x${c}` }));

const toNum = (v) => (v === '' || v == null ? null : Number(v));

// Модалка точки ярмарки: параметры, цена/коэффициент, QR, медиа. Для точек на модерации — блок одобрения снизу.
export default function PointModal({ point, fairState, onClose }) {
    const { enqueueSnackbar } = useSnackbar();
    const [modComment, setModComment] = useState('');
    const isPending = point.status === POINT_STATUS.PENDING;
    const [data, setData] = useState({
        name: point.name || '',
        description: point.description || '',
        code: point.code || '',
        type: point.type || '',
        price: point.price || '',
        winCoefficient: point.winCoefficient || '',
        maxVisitors: point.maxVisitors || '',
        serviceDuration: point.serviceDuration || ''
    });
    const [media, setMedia] = useState(point.mediaFiles || []);
    const [pending, setPending] = useState([]);

    const set = (field) => (e) => setData(prev => ({ ...prev, [field]: e.target.value }));
    const isChance = data.type === POINT_TYPE.CHANCE;
    const qrValue = fairState.game?.qrBaseUrl ? `${fairState.game.qrBaseUrl}${data.code}` : data.code;
    const chip = pointStatusChip(point.status);

    const handleSave = async () => {
        try {
            const existing = media.filter(it => !it.__pending);
            const payload = {
                name: data.name,
                description: data.description,
                code: data.code,
                type: data.type,
                price: toNum(data.price),
                winCoefficient: toNum(data.winCoefficient),
                maxVisitors: toNum(data.maxVisitors),
                serviceDuration: toNum(data.serviceDuration),
                mediaList: existing
            };
            await fairState.savePoint(point.id, payload, pending);
            setPending([]);
            onClose();
        } catch (e) { /* ошибку покажет хук */ }
    };

    const handleDelete = async () => {
        onClose();
        await fairState.deletePoint(point.id);
    };

    const approve = async () => {
        try { await fairState.approvePoint(point.id); onClose(); } catch (e) { /* ошибку покажет хук */ }
    };
    const reject = async () => {
        if (!modComment.trim()) {
            enqueueSnackbar('Укажите причину отклонения', { variant: 'warning' });
            return;
        }
        try { await fairState.requestChanges(point.id, modComment.trim()); onClose(); } catch (e) { /* ошибку покажет хук */ }
    };

    const footer = (
        <>
            <button type="button" className="btn btn-danger" style={{ marginRight: 'auto' }} onClick={handleDelete}>Удалить</button>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Отмена</button>
            <button type="button" className="btn btn-primary" onClick={handleSave}>Сохранить</button>
        </>
    );

    return (
        <Modal open kicker="Точка" title={point.name ? 'Редактировать точку' : 'Новая точка'} size="lg" onClose={onClose} footer={footer}>
            {point.moderationComment && <div className="fr-modcomment">Комментарий модерации: {point.moderationComment}</div>}

            <div className="fr-mtop">
                <span className={chip.cls}>{chip.label}</span>
                {data.winCoefficient && <span className="fr-chip-win">выигрыш ×{data.winCoefficient}</span>}
                <span className="fr-pspent"><CoinIcon />Заработано: {point.spent || 0}</span>
            </div>

            <LabeledInput label="Название точки" value={data.name} onChange={set('name')} />
            <div>
                <label className="ctl-label">Описание</label>
                <textarea className="textarea" value={data.description} onChange={set('description')} />
            </div>

            <div className="fr-grid2">
                <LabeledSelect label="Тип точки" value={data.type} onChange={set('type')} options={TYPE_OPTIONS} placeholder="Выберите тип" />
                <LabeledSelect label="Цена (монет)" value={data.price} onChange={set('price')} options={PRICE_OPTIONS} placeholder="Выберите цену" />
            </div>
            <div className="fr-grid2">
                <LabeledInput label="Макс. посетителей" type="number" value={String(data.maxVisitors)} onChange={set('maxVisitors')} />
                <LabeledInput label="Время услуги (мин)" type="number" value={String(data.serviceDuration)} onChange={set('serviceDuration')} />
            </div>
            {isChance && (
                <LabeledSelect label="Коэф. выигрыша (для азартных)" value={data.winCoefficient} onChange={set('winCoefficient')} options={COEF_OPTIONS} placeholder="напр. 2" />
            )}

            <div className="fr-qrbox">
                <div className="fr-qrbig">{data.code && <QRCodeSVG value={qrValue} size={110} level="M" />}</div>
                <div className="qrr">
                    <label className="ctl-label">Код точки</label>
                    <input className="input" value={data.code} onChange={set('code')} />
                    <div className="fr-qrhint">При изменении QR перегенерируется автоматически</div>
                </div>
            </div>

            <div>
                <div className="section-label">Медиа</div>
                <AppMediaList value={media} pendingFiles={pending} onChange={(v, p) => { setMedia(v); setPending(p); }} type="ALL" />
            </div>

            {isPending && (
                <div className="fr-modsect">
                    <div className="section-label">Модерация</div>
                    <label className="ctl-label">Комментарий (причина отклонения)</label>
                    <textarea className="textarea" value={modComment} onChange={(e) => setModComment(e.target.value)} placeholder="Укажите, что поправить команде" />
                    <div className="fr-modacts">
                        <button type="button" className="btn btn-danger" onClick={reject}>Отклонить</button>
                        <button type="button" className="btn btn-ok" onClick={approve}>Одобрить</button>
                    </div>
                </div>
            )}
        </Modal>
    );
}

PointModal.propTypes = {
    point: PropTypes.object.isRequired,
    fairState: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired
};
